// The home-loan engine (pure): replays the loans (loans.js) day by day from
// the first settlement — history and projection in one pass.
//
// Each day, for every ACTIVE loan (settled, not yet closed — a refinance closes
// the old loan the day the new one settles): interest accrues daily on each
// portion's balance (less the offset, applied to the `offset` portions in
// order), and on the loan's `repaymentDay` (clamped to the month's length) the
// accrued interest is charged (rounded to the cent) and the repayment taken —
// the minimum that clears the portion over its remaining term, worked out on
// the first repayment day and again after each rate change (like a bank: an
// extra repayment shortens the term rather than lowering the repayment; past
// the term, whatever is left is due). Repayments come OUT of the offset (which never goes
// below zero); contribution plans pay INTO it on their cadence. A `balance`
// event / offset `balance` entry resets the model to the real figure at the
// end of that day, recording the difference.

import { addDays, monthLength, toYmd } from './fy.js';
import { cadenceDates } from './fund-deposits.js';

/** How far past `today` a projection runs at most, in years. */
export const MAX_PROJECTION_YEARS = 40;
const DAYS_PER_YEAR = 365;

/**
 * The monthly repayment that clears `balanceCents` over `months` at
 * `annualRatePct` (rounded up to the cent; the whole balance when no months
 * are left).
 */
export function minimumRepayment(balanceCents, annualRatePct, months) {
  if (balanceCents <= 0) return 0;
  if (months <= 0) return balanceCents;
  const r = annualRatePct / 100 / 12;
  if (r === 0) return Math.ceil(balanceCents / months);
  return Math.ceil((balanceCents * r) / (1 - Math.pow(1 + r, -months)));
}

/** Whole months from `a` to `b`. */
export function monthsBetween(a, b) {
  const A = toYmd(a);
  const B = toYmd(b);
  return (B.y - A.y) * 12 + (B.m - A.m) - (B.d < A.d ? 1 : 0);
}

function isRepaymentDay(date, day) {
  const { y, m, d } = toYmd(date);
  return d === Math.min(day, monthLength(y, m));
}

function groupByDate(rows) {
  const map = new Map();
  for (const r of rows) {
    if (!map.has(r.date)) map.set(r.date, []);
    map.get(r.date).push(r);
  }
  return map;
}

/** Each day's contributions into the offset (cents), from `from` to `to`. */
export function contributionsByDate(plans, employments, from, to) {
  const out = new Map();
  const sorted = [...plans].sort((a, b) => a.from.localeCompare(b.from));
  sorted.forEach((plan, i) => {
    const next = sorted[i + 1]?.from;
    const a = plan.from > from ? plan.from : from;
    const end = next ? addDays(next, -1) : to;
    const b = end < to ? end : to;
    for (const { amount, period } of Object.values(plan.amounts)) {
      for (const date of cadenceDates({ from: plan.from, period }, employments, a, b)) {
        out.set(date, (out.get(date) ?? 0) + amount.amount);
      }
    }
  });
  return out;
}

/** Split `cents` across portions by balance (all to the first when none owed). */
function spread(portions, cents, apply) {
  const total = portions.reduce((s, p) => s + p.bal, 0);
  let left = cents;
  portions.forEach((p, i) => {
    const share =
      i === portions.length - 1
        ? left
        : total > 0
          ? Math.round((cents * p.bal) / total)
          : i === 0
            ? cents
            : 0;
    left -= share;
    apply(p, share);
  });
}

/**
 * Replay the loans. `today` splits history from projection; the run stops at
 * payoff, at `until`, or `MAX_PROJECTION_YEARS` after `today`.
 * @returns {null | { points: [{ date, balanceCents, offsetCents, interestCents }],
 *   payoffDate, coveredDate, interestToDateCents, interestRemainingCents,
 *   anchors: [{ date, loanRef, portionId, modelledCents, actualCents, diffCents }],
 *   offsetAnchors: [{ date, modelledCents, actualCents, diffCents, opening }],
 *   (`opening` = the offset's first figure, before any money moved — not drift);
 *   refinances: [{ date, fromRef, toRef, payoutCents, principalCents }], now }}
 *   `points` = each repayment day, settlement and anchor (cumulative interest);
 *   `now` = the position at the end of `today`.
 */
export function simulateLoans({
  loans,
  events = [],
  offset = [],
  contributions = [],
  employments = [],
  today,
  until = null,
}) {
  if (!loans.length) return null;
  const sorted = [...loans].sort((a, b) => a.settlementDate.localeCompare(b.settlementDate));
  const start = sorted[0].settlementDate;
  const lastSettlement = sorted.at(-1).settlementDate;
  const horizon = addDays(
    today > lastSettlement ? today : lastSettlement,
    MAX_PROJECTION_YEARS * 366,
  );
  const end = until && until < horizon ? until : horizon;

  const eventsOn = groupByDate(events);
  const offsetOn = groupByDate(offset);
  const contributed = contributionsByDate(contributions, employments, start, end);
  const states = sorted.map((loan) => ({
    loan,
    active: false,
    closed: false,
    portions: loan.portions.map((p) => ({
      id: p.id,
      def: p,
      bal: 0,
      rate: 0,
      repayment: null,
      accrued: 0,
    })),
  }));
  const byRef = new Map(states.map((s) => [s.loan.itemId, s]));

  let offsetCents = 0;
  // Whether anything has touched the offset yet (its first statement is its opening balance).
  let offsetSeen = false;
  let interestCents = 0;
  let interestToDateCents = 0;
  let payoffDate = null;
  let coveredDate = null;
  let now = null;
  const points = [];
  const anchors = [];
  const offsetAnchors = [];
  const refinances = [];
  const totalOwed = () =>
    states.reduce((s, st) => s + (st.active ? st.portions.reduce((t, p) => t + p.bal, 0) : 0), 0);
  const point = (date) => {
    const p = { date, balanceCents: totalOwed(), offsetCents, interestCents };
    if (points.at(-1)?.date === date) points[points.length - 1] = p;
    else points.push(p);
    return p;
  };
  const targets = (st, portionId) =>
    portionId ? st.portions.filter((p) => p.id === portionId) : st.portions;

  for (let d = start; d <= end; d = addDays(d, 1)) {
    let changed = false;
    // Closures, then settlements (a refinance pays the old loan out today).
    for (const st of states) {
      if (st.active && st.loan.closedOn && st.loan.closedOn <= d) {
        st.active = false;
        st.closed = true;
        st.payoutCents = st.portions.reduce((s, p) => s + p.bal, 0);
        changed = true;
      }
    }
    for (const st of states) {
      if (st.closed || st.active || st.loan.settlementDate !== d) continue;
      st.active = true;
      for (const p of st.portions) p.bal = p.def.principal.amount;
      const from = st.loan.refinancedFrom ? byRef.get(st.loan.refinancedFrom) : null;
      if (from) {
        refinances.push({
          date: d,
          fromRef: from.loan.itemId,
          toRef: st.loan.itemId,
          payoutCents: from.payoutCents ?? from.portions.reduce((s, p) => s + p.bal, 0),
          principalCents: st.portions.reduce((s, p) => s + p.bal, 0),
        });
      }
      changed = true;
    }

    // Money into / out of the offset.
    const contributedToday = contributed.get(d) ?? 0;
    offsetCents += contributedToday;
    if (contributedToday) offsetSeen = true;
    const offsetBalances = [];
    for (const e of offsetOn.get(d) ?? []) {
      if (e.kind === 'balance') {
        offsetBalances.push(e);
        continue;
      }
      offsetSeen = true;
      if (e.kind === 'deposit') offsetCents += e.amount.amount;
      else offsetCents = Math.max(0, offsetCents - e.amount.amount);
    }

    // Loan events (balance anchors wait for the end of the day).
    const balanceEvents = [];
    for (const e of eventsOn.get(d) ?? []) {
      const st = byRef.get(e.loanRef);
      if (!st?.active) continue;
      const ps = targets(st, e.portionId);
      if (e.type === 'rate') {
        // A rate change resets the repayment (worked out on the next repayment day).
        for (const p of ps) {
          p.rate = e.rate;
          p.repayment = null;
        }
      } else if (e.type === 'extra') {
        spread(ps, e.amount.amount, (p, c) => (p.bal = Math.max(0, p.bal - c)));
        if (e.fromOffset !== false) offsetCents = Math.max(0, offsetCents - e.amount.amount);
      } else if (e.type === 'redraw') {
        spread(ps, e.amount.amount, (p, c) => (p.bal += c));
      } else balanceEvents.push({ e, st });
    }

    // Interest accrues on today's balance (less the offset).
    let offsetLeft = offsetCents;
    for (const st of states) {
      if (!st.active) continue;
      for (const p of st.portions) {
        if (p.bal <= 0) continue;
        let base = p.bal;
        if (p.def.offset) {
          base = Math.max(0, p.bal - offsetLeft);
          offsetLeft = Math.max(0, offsetLeft - p.bal);
        }
        p.accrued += (base * p.rate) / 100 / DAYS_PER_YEAR;
      }
    }

    // Repayment day: charge the interest, take the repayment from the offset.
    for (const st of states) {
      if (!st.active || d === st.loan.settlementDate) continue;
      if (!isRepaymentDay(d, st.loan.repaymentDay)) continue;
      // Repayments left including today's (the first is month 1 of the term).
      const monthsLeft = (p) => p.def.termYears * 12 - monthsBetween(st.loan.settlementDate, d) + 1;
      for (const p of st.portions) {
        const charge = Math.round(p.accrued);
        p.accrued = 0;
        p.bal += charge;
        interestCents += charge;
        if (d <= today) interestToDateCents += charge;
        if (p.bal <= 0) continue;
        const left = monthsLeft(p);
        if (p.repayment === null || left <= 0) {
          p.repayment = minimumRepayment(p.bal, p.rate, left);
        }
        const pay = Math.min(p.repayment, p.bal);
        p.bal -= pay;
        offsetCents = Math.max(0, offsetCents - pay);
      }
      changed = true;
    }

    // End of day: the real figures win.
    for (const { e, st } of balanceEvents) {
      const ps = targets(st, e.portionId);
      const modelled = ps.reduce((s, p) => s + p.bal, 0);
      spread(ps, e.amount.amount, (p, c) => (p.bal = c));
      anchors.push({
        date: d,
        loanRef: st.loan.itemId,
        portionId: e.portionId,
        modelledCents: modelled,
        actualCents: e.amount.amount,
        diffCents: e.amount.amount - modelled,
      });
      changed = true;
    }
    for (const e of offsetBalances) {
      offsetAnchors.push({
        date: d,
        modelledCents: offsetCents,
        actualCents: e.amount.amount,
        diffCents: e.amount.amount - offsetCents,
        opening: !offsetSeen,
      });
      offsetSeen = true;
      offsetCents = e.amount.amount;
      changed = true;
    }

    const owed = totalOwed();
    if (!coveredDate && d >= lastSettlement && owed > 0 && offsetCents >= owed) coveredDate = d;
    if (changed) point(d);
    if (d === today) now = point(d);
    if (d >= lastSettlement && owed <= 0 && states.some((s) => s.active)) {
      payoffDate = d;
      point(d);
      break;
    }
  }

  return {
    points,
    payoffDate,
    coveredDate,
    interestToDateCents,
    interestRemainingCents: interestCents - interestToDateCents,
    anchors,
    offsetAnchors,
    refinances,
    now,
  };
}
